export const PRIVILEGES_VERSION_HEADER = 'X-Privileges-Version';

type Listener = () => void;

export type PrivilegesVersionTracker = {
    observe: (version: string | null | undefined) => void;
    markUnapplied: () => void;
    subscribe: (listener: Listener) => () => void;
};

// The backend stamps every authenticated response with the tenant's privileges version and
// moves it when a role or a role assignment changes. The first value seen is the baseline for
// the running session; only a later, different value means the user's permissions may have
// changed since they were loaded.
export function createPrivilegesVersionTracker(): PrivilegesVersionTracker {
    let lastSeen: string | null = null;
    // A refresh that failed leaves the current version unapplied, so the next response carrying
    // that same version notifies again instead of being taken as already handled.
    let unapplied = false;
    const listeners = new Set<Listener>();

    return {
        observe(version) {
            if (version == null) return;
            if (version === lastSeen && !unapplied) return;
            const isBaseline = lastSeen === null;
            lastSeen = version;
            unapplied = false;
            if (isBaseline) return;
            listeners.forEach((listener) => listener());
        },
        markUnapplied() {
            unapplied = true;
        },
        subscribe(listener) {
            listeners.add(listener);
            return () => {
                listeners.delete(listener);
            };
        },
    };
}

export const privilegesVersionTracker = createPrivilegesVersionTracker();

export function observePrivilegesVersionHeader(headers: Headers | undefined): void {
    privilegesVersionTracker.observe(headers?.get(PRIVILEGES_VERSION_HEADER));
}
